import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Spinner } from '../ui/spinner';
import { ArrowLeft } from 'lucide-react';
import { notificationAPI } from '../../services/api';
import { useNotifications, SEVERITY_CONFIG } from '../../context/NotificationContext';
import { formatMessage } from '../../utils/messageFormatter';
import { PayloadFields, PayloadActions } from './PayloadParts';

export default function NotificationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { decrementUnread } = useNotifications();

  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const markedRef = useRef(new Set());

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    setNotification(null);

    notificationAPI.getById(id)
      .then((res) => {
        if (!alive) return;
        const n = res.data;
        setNotification(n);
        if (n && !n.is_read && !markedRef.current.has(id)) {
          markedRef.current.add(id);
          notificationAPI.markRead(id)
            .then(() => decrementUnread(1))
            .catch(() => {});
        }
      })
      .catch((err) => {
        if (!alive) return;
        setError(err.response?.status === 404 ? 'not-found' : 'error');
      })
      .finally(() => { if (alive) setLoading(false); });

    return () => { alive = false; };
  }, [id, decrementUnread]);

  const fmtTime = (iso) => new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  return (
    <div className="max-w-[860px] mx-auto">
      <Button variant="ghost" size="sm" onClick={() => navigate('/notifications')} className="gap-1.5 text-xs mb-4 -ml-2">
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to notifications
      </Button>

      {loading ? (
        <div className="flex justify-center py-16">
          <Spinner size="sm" />
        </div>
      ) : error === 'not-found' ? (
        <div className="py-16 text-center">
          <p className="text-sm text-ink-dim mb-3">Notification not found</p>
          <Button variant="outline" size="sm" onClick={() => navigate('/notifications')}>Go to notifications</Button>
        </div>
      ) : error ? (
        <div className="py-16 text-center">
          <p className="text-sm text-ink-dim mb-3">Failed to load notification</p>
          <Button variant="outline" size="sm" onClick={() => navigate('/notifications')}>Go to notifications</Button>
        </div>
      ) : notification ? (
        <NotificationDetail notification={notification} fmtTime={fmtTime} />
      ) : null}
    </div>
  );
}

function NotificationDetail({ notification: n, fmtTime }) {
  const cfg = SEVERITY_CONFIG[n.severity] || SEVERITY_CONFIG.info;

  return (
    <div
      className="flex gap-0 rounded-xl border overflow-hidden bg-surface-1"
      style={{ borderColor: cfg.color + '40' }}
    >
      <div
        style={{
          width: 4,
          flexShrink: 0,
          background: cfg.color,
        }}
      />

      <div className="flex-1 p-5">
        <div className="flex items-center gap-1.5 mb-3 flex-wrap">
          <span
            className="text-[9px] font-extrabold tracking-[.07em] uppercase px-2 py-0.5 rounded-[5px] text-white leading-none"
            style={{ background: cfg.color }}
          >
            {cfg.label}
          </span>
          <span className="text-[9px] font-bold tracking-[.05em] uppercase px-2 py-0.5 rounded-[5px] bg-white/[.07] text-ink-dim leading-none">
            {n.brand?.toUpperCase()}
          </span>
          <span className="text-[11px] text-ink-mute ml-auto shrink-0">{fmtTime(n.created_at)}</span>
        </div>

        {n.payload?.title && (
          <h1 className="text-lg font-bold text-ink leading-snug mb-4 tracking-[-0.1px]">
            {n.payload.title}
          </h1>
        )}

        {n.payload?.message && (
          <div className="mb-4 text-sm text-ink-dim leading-relaxed">
            {formatMessage(n.payload.message)}
          </div>
        )}

        <PayloadFields fields={n.payload?.fields} />
        <PayloadActions actions={n.payload?.actions} />
      </div>
    </div>
  );
}
